import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { DateInput } from '@/components/ui/date-input'
import { useToast } from '@/hooks/useToast'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { tasksApi } from '@/lib/api/tasks'
import { extractApiError } from '@/lib/api'
import { convertToApiFormat, formatDate, formatDateForInput } from '@/lib/utils'
import type { TaskSummary, UpdateTaskDto } from '@/types'

interface TaskInfoPanelProps {
  task: TaskSummary
}

type EditField = 'name' | 'comment' | 'extra_attribute' | 'production_date' | 'expiry_date'

export function TaskInfoPanel({ task }: TaskInfoPanelProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data: flags } = useFeatureFlags()
  const showExtra = Boolean(flags?.enable_task_extra_attribute)

  const [name, setName] = useState(task.name)
  const [comment, setComment] = useState(task.comment ?? '')
  const [extraAttribute, setExtraAttribute] = useState(task.extra_attribute ?? '')
  const [productionDate, setProductionDate] = useState(formatDateForInput(task.production_date))
  const [expiryDate, setExpiryDate] = useState(formatDateForInput(task.expiry_date))
  const snapshot = useRef({
    name: task.name,
    comment: task.comment ?? '',
    extraAttribute: task.extra_attribute ?? '',
    productionDate: formatDateForInput(task.production_date),
    expiryDate: formatDateForInput(task.expiry_date),
  })
  const [editing, setEditing] = useState<null | EditField>(null)
  const editContainerRef = useRef<HTMLDivElement>(null)
  const finishTimerRef = useRef<number | null>(null)

  useEffect(() => {
    setName(task.name)
    setComment(task.comment ?? '')
    setExtraAttribute(task.extra_attribute ?? '')
    setProductionDate(formatDateForInput(task.production_date))
    setExpiryDate(formatDateForInput(task.expiry_date))
    snapshot.current = {
      name: task.name,
      comment: task.comment ?? '',
      extraAttribute: task.extra_attribute ?? '',
      productionDate: formatDateForInput(task.production_date),
      expiryDate: formatDateForInput(task.expiry_date),
    }
  }, [task.uuid, task.name, task.comment, task.extra_attribute, task.production_date, task.expiry_date])

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['task', task.uuid] })
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: ['task-km-codes', task.uuid] })
    void queryClient.invalidateQueries({ queryKey: ['task-boxes', task.uuid] })
    void queryClient.invalidateQueries({ queryKey: ['task-pallets', task.uuid] })
  }

  const metaMutation = useMutation({
    mutationFn: (data: UpdateTaskDto) => tasksApi.update(task.uuid, data),
    onSuccess: () => {
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  const datesMutation = useMutation({
    mutationFn: (data: { production_date?: string; expiry_date?: string }) =>
      tasksApi.assignDates(task.uuid, { scope: 'task', ...data }),
    onSuccess: (res) => {
      const n = res.data.updated_count
      toast.success(n > 0 ? `Обновлено кодов: ${n}` : 'Даты задания сохранены')
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err)),
  })

  const saveMeta = (field: EditField) => {
    const nextName = name.trim()
    const nextComment = comment.trim()
    const nextExtra = extraAttribute.trim()
    const payload: UpdateTaskDto = {}
    if (nextName !== snapshot.current.name) payload.name = nextName
    if (nextComment !== snapshot.current.comment) payload.comment = nextComment
    if (showExtra && nextExtra !== snapshot.current.extraAttribute) payload.extra_attribute = nextExtra
    if (Object.keys(payload).length === 0) return
    metaMutation.mutate(payload, {
      onSuccess: () => {
        snapshot.current.name = nextName
        snapshot.current.comment = nextComment
        snapshot.current.extraAttribute = nextExtra
        setEditing((cur) => (cur === field ? null : cur))
      },
    })
  }

  const saveDates = (field: EditField) => {
    const prod = convertToApiFormat(productionDate)
    const exp = convertToApiFormat(expiryDate)
    const snapProd = convertToApiFormat(snapshot.current.productionDate)
    const snapExp = convertToApiFormat(snapshot.current.expiryDate)
    const payload: { production_date?: string; expiry_date?: string } = {}
    if (prod && prod !== snapProd) payload.production_date = prod
    if (exp && exp !== snapExp) payload.expiry_date = exp
    if (Object.keys(payload).length === 0) return
    if (payload.production_date) snapshot.current.productionDate = payload.production_date
    if (payload.expiry_date) snapshot.current.expiryDate = payload.expiry_date
    datesMutation.mutate(payload, {
      onSuccess: () => {
        setEditing((cur) => (cur === field ? null : cur))
      },
    })
  }

  const finishEditing = useCallback(() => {
    if (!editing) return
    const field = editing
    if (field === 'production_date' || field === 'expiry_date') {
      saveDates(field)
    } else {
      saveMeta(field)
    }
    setEditing(null)
  }, [editing, name, comment, extraAttribute, productionDate, expiryDate, showExtra])

  const scheduleFinishEditing = useCallback(() => {
    if (finishTimerRef.current != null) {
      window.clearTimeout(finishTimerRef.current)
    }
    finishTimerRef.current = window.setTimeout(() => {
      finishTimerRef.current = null
      finishEditing()
    }, 0)
  }, [finishEditing])

  useEffect(() => {
    if (!editing) return
    const onPointerDown = (e: PointerEvent) => {
      if (editContainerRef.current?.contains(e.target as Node)) return
      scheduleFinishEditing()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      if (finishTimerRef.current != null) {
        window.clearTimeout(finishTimerRef.current)
        finishTimerRef.current = null
      }
    }
  }, [editing, scheduleFinishEditing])

  const fieldLabel = 'block text-xs font-medium text-gray-500 mb-1'
  const valueText =
    'font-medium text-gray-900 min-h-[2.25rem] flex items-center rounded-md border border-transparent px-3 py-2 hover:bg-gray-50 cursor-pointer'
  const displayOrDash = (v: string) => (v.trim() ? v : '—')
  const displayDateValue = (local: string, api?: string) =>
    local.trim() || (api ? formatDate(api) : '') || '—'

  const cancelEdit = () => {
    setName(snapshot.current.name)
    setComment(snapshot.current.comment)
    setExtraAttribute(snapshot.current.extraAttribute)
    setProductionDate(snapshot.current.productionDate)
    setExpiryDate(snapshot.current.expiryDate)
    setEditing(null)
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold mb-4">Информация</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <label className={fieldLabel} htmlFor={`task-name-${task.uuid}`}>
            Название
          </label>
          {editing === 'name' ? (
            <div ref={editContainerRef}>
            <Input
              id={`task-name-${task.uuid}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  saveMeta('name')
                  setEditing(null)
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  cancelEdit()
                }
              }}
              autoFocus
              disabled={metaMutation.isPending}
            />
            </div>
          ) : (
            <div
              className={valueText}
              role="button"
              tabIndex={0}
              onClick={() => setEditing('name')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setEditing('name')
                }
              }}
            >
              {displayOrDash(name)}
            </div>
          )}
        </div>
        <div>
          <label className={fieldLabel} htmlFor={`task-comment-${task.uuid}`}>
            Комментарий
          </label>
          {editing === 'comment' ? (
            <div ref={editContainerRef}>
            <textarea
              id={`task-comment-${task.uuid}`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  cancelEdit()
                }
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault()
                  saveMeta('comment')
                  setEditing(null)
                }
              }}
              rows={2}
              disabled={metaMutation.isPending}
              autoFocus
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              placeholder="Заметка для печати или учёта"
            />
            </div>
          ) : (
            <div
              className={valueText}
              role="button"
              tabIndex={0}
              onClick={() => setEditing('comment')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setEditing('comment')
                }
              }}
            >
              <span className="whitespace-pre-wrap">{displayOrDash(comment)}</span>
            </div>
          )}
        </div>
        <div>
          <label className={fieldLabel} htmlFor={`task-prod-${task.uuid}`}>
            Дата производства
          </label>
          {editing === 'production_date' ? (
            <div ref={editContainerRef}>
            <DateInput
              id={`task-prod-${task.uuid}`}
              value={productionDate}
              onChange={setProductionDate}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  cancelEdit()
                }
                if (e.key === 'Enter') {
                  e.preventDefault()
                  saveDates('production_date')
                  setEditing(null)
                }
              }}
              autoFocus
              disabled={datesMutation.isPending}
            />
            </div>
          ) : (
            <div
              className={valueText}
              role="button"
              tabIndex={0}
              onClick={() => setEditing('production_date')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setEditing('production_date')
                }
              }}
            >
              {displayDateValue(productionDate, task.production_date)}
            </div>
          )}
        </div>
        <div>
          <label className={fieldLabel} htmlFor={`task-exp-${task.uuid}`}>
            Срок годности
          </label>
          {editing === 'expiry_date' ? (
            <div ref={editContainerRef}>
            <DateInput
              id={`task-exp-${task.uuid}`}
              value={expiryDate}
              onChange={setExpiryDate}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  cancelEdit()
                }
                if (e.key === 'Enter') {
                  e.preventDefault()
                  saveDates('expiry_date')
                  setEditing(null)
                }
              }}
              autoFocus
              disabled={datesMutation.isPending}
            />
            </div>
          ) : (
            <div
              className={valueText}
              role="button"
              tabIndex={0}
              onClick={() => setEditing('expiry_date')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setEditing('expiry_date')
                }
              }}
            >
              {displayDateValue(expiryDate, task.expiry_date)}
            </div>
          )}
        </div>
        {showExtra ? (
          <div>
            <label className={fieldLabel} htmlFor={`task-extra-${task.uuid}`}>
              Доп. реквизит
            </label>
            {editing === 'extra_attribute' ? (
              <div ref={editContainerRef}>
              <Input
                id={`task-extra-${task.uuid}`}
                value={extraAttribute}
                onChange={(e) => setExtraAttribute(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    saveMeta('extra_attribute')
                    setEditing(null)
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    cancelEdit()
                  }
                }}
                autoFocus
                disabled={metaMutation.isPending}
                placeholder="Дополнительный реквизит для этикеток"
              />
              </div>
            ) : (
              <div
                className={valueText}
                role="button"
                tabIndex={0}
                onClick={() => setEditing('extra_attribute')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEditing('extra_attribute')
                  }
                }}
              >
                {displayOrDash(extraAttribute)}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
